/**
 * API client for the Flask insights backend (app.py).
 * In dev, use proxy (empty base) so requests go to same origin and get proxied to Flask.
 * Set REACT_APP_INSIGHTS_API_URL to override (e.g. production backend).
 */

const API_BASE =
  process.env.REACT_APP_INSIGHTS_API_URL !== undefined && process.env.REACT_APP_INSIGHTS_API_URL !== ''
    ? process.env.REACT_APP_INSIGHTS_API_URL.replace(/\/$/, '')
    : ''; // In dev, use proxy (empty base) so requests go to same origin and get proxied to Flask.

function getAuthHeaders() {
  const token = localStorage.getItem('insights_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function searchOrganizations(orgName) {
  const trimmed = (orgName || '').trim();
  if (!trimmed) {
    return [];
  }
  const url = API_BASE
    ? `${API_BASE}/organizations?org_name=${encodeURIComponent(trimmed)}`
    : `/organizations?org_name=${encodeURIComponent(trimmed)}`;
  let res;
  try {
    res = await fetch(url, { headers: getAuthHeaders() });
  } catch (netErr) {
    const msg =
      netErr.message && netErr.message.includes('Failed to fetch')
        ? 'Backend unavailable. Start the Flask app (e.g. python app.py) on port 5001.'
        : netErr.message || 'Network error';
    throw new Error(msg);
  }
  if (!res.ok) {
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent('auth-error'));
    }
    const err = await res.json().catch(() => ({}));
    const message =
      res.status === 500
        ? 'Server error. Check that the database is set up and the Flask app is running.'
        : err.error || `Search failed: ${res.status}`;
    throw new Error(message);
  }
  return res.json();
}

export async function searchSchools(schoolName) {
  const trimmed = (schoolName || '').trim();
  if (!trimmed) {
    return [];
  }
  const url = API_BASE
    ? `${API_BASE}/schools?school_name=${encodeURIComponent(trimmed)}`
    : `/schools?school_name=${encodeURIComponent(trimmed)}`;
  let res;
  try {
    res = await fetch(url, { headers: getAuthHeaders() });
  } catch (netErr) {
    const msg =
      netErr.message && netErr.message.includes('Failed to fetch')
        ? 'Backend unavailable. Start the Flask app (e.g. python app.py) on port 5001.'
        : netErr.message || 'Network error';
    throw new Error(msg);
  }
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    const message =
      res.status === 500
        ? 'Server error. Check that the database is set up and the Flask app is running.'
        : err.error || `School search failed: ${res.status}`;
    throw new Error(message);
  }
  return res.json();
}

/**
 * Build connection_filters query param (JSON) for org-transitions and alumni.
 * All arrays: empty = no filter (All / Any). Multiple = OR within category, AND across.
 */
export function buildConnectionFiltersParam(filters) {
  if (!filters) return null;
  const payload = {
    past_companies: filters.past_companies || [],
    past_roles: filters.past_roles || [],
    tenure_options: filters.tenure_options || [],
    colleges: filters.colleges || [],
    departments: filters.departments || [],
    batch_options: filters.batch_options || [],
  };
  return JSON.stringify(payload);
}

export async function getOrgTransitions(orgId, { startDate, endDate, hops = 3, role, connectionFilters, includeRelated, includeRelatedDetails = false } = {}) {
  const id = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) {
    throw new Error('org_id is required');
  }
  const params = new URLSearchParams({ org_id: String(id), hops: String(hops) });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (role) params.set('role', role);
  if (includeRelated) params.set('include_related', '1');
  if (includeRelated && includeRelatedDetails) params.set('include_related_details', '1');
  const cf = buildConnectionFiltersParam(connectionFilters);
  if (cf) params.set('connection_filters', cf);
  const url = API_BASE
    ? `${API_BASE}/org-transitions?${params.toString()}`
    : `/org-transitions?${params.toString()}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Transitions failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch employees who transitioned from a source org to a destination org
 * in exactly `hop` moves within an optional date range and optional role filter.
 */
export async function getEmployeeTransitions(
  sourceOrgId,
  destOrgId,
  { hop, startDate, endDate, role, location, connectionFilters, includeMatchDetails = false } = {}
) {
  const source = sourceOrgId != null ? Number(sourceOrgId) : null;
  const dest = destOrgId != null ? Number(destOrgId) : null;
  const hopNum = hop != null ? Number(hop) : null;

  if (source == null || Number.isNaN(source)) {
    throw new Error('source_org_id is required');
  }
  if (dest == null || Number.isNaN(dest)) {
    throw new Error('dest_org_id is required');
  }
  if (hopNum == null || Number.isNaN(hopNum) || hopNum < 1) {
    throw new Error('hop must be a positive integer');
  }

  const params = new URLSearchParams({
    source_org_id: String(source),
    dest_org_id: String(dest),
    hop: String(hopNum),
  });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (role) params.set('role', role);
  if (location) params.set('location', location);
  const cf = buildConnectionFiltersParam(connectionFilters);
  if (cf) params.set('connection_filters', cf);
  params.set('include_match_details', includeMatchDetails ? '1' : '0');

  const url = API_BASE
    ? `${API_BASE}/employee-transitions?${params.toString()}`
    : `/employee-transitions?${params.toString()}`;

  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Employee transitions failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch all alumni for a given organization (people who worked there and left).
 * Optional connectionFilters filters by profile-based connections.
 */
export async function getAlumni(orgId, { startDate, endDate, connectionFilters, page = 1, limit = 50 } = {}) {
  const id = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) {
    throw new Error('org_id is required');
  }
  const params = new URLSearchParams({ org_id: String(id) });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const cf = buildConnectionFiltersParam(connectionFilters);
  if (cf) params.set('connection_filters', cf);
  params.set('page', String(Math.max(1, Number(page) || 1)));
  params.set('limit', String(Math.max(1, Math.min(200, Number(limit) || 50))));

  const url = API_BASE
    ? `${API_BASE}/alumni?${params.toString()}`
    : `/alumni?${params.toString()}`;

  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch alumni: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch current employees for a given organization.
 * Optional connectionFilters annotates profile-based matches.
 */
export async function getCurrentEmployees(orgId, { startDate, endDate, connectionFilters, page = 1, limit = 50 } = {}) {
  const id = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) {
    throw new Error('org_id is required');
  }
  const params = new URLSearchParams({ org_id: String(id) });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const cf = buildConnectionFiltersParam(connectionFilters);
  if (cf) params.set('connection_filters', cf);
  params.set('page', String(Math.max(1, Number(page) || 1)));
  params.set('limit', String(Math.max(1, Math.min(200, Number(limit) || 50))));

  const url = API_BASE
    ? `${API_BASE}/employees?${params.toString()}`
    : `/employees?${params.toString()}`;

  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch employees: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch dashboard data in one call: filter_options, transitions, alumni (no connection filters).
 */
export async function getDashboardData(orgId, { startDate, endDate, hops = 3, role } = {}) {
  const id = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) {
    throw new Error('org_id is required');
  }
  const params = new URLSearchParams({ org_id: String(id), hops: String(hops) });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (role) params.set('role', role);
  params.set('include_related_details', '0');
  const url = API_BASE
    ? `${API_BASE}/dashboard-data?${params.toString()}`
    : `/dashboard-data?${params.toString()}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Dashboard data failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch related-background people at a destination company (not from transition).
 * Optional connectionFilters annotates filter matches (is_match + filter_match_details).
 */
export async function getRelatedBackground(sourceOrgId, destOrgId, { startDate, endDate, location, connectionFilters } = {}) {
  const source = sourceOrgId != null ? Number(sourceOrgId) : null;
  const dest = destOrgId != null ? Number(destOrgId) : null;
  if (dest == null || Number.isNaN(dest)) {
    throw new Error('dest_org_id is required');
  }
  const params = new URLSearchParams({ dest_org_id: String(dest) });
  if (source != null && !Number.isNaN(source)) params.set('source_org_id', String(source));
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (location) params.set('location', location);
  const cf = buildConnectionFiltersParam(connectionFilters);
  if (cf) params.set('connection_filters', cf);
  params.set('include_history', '0');
  params.set('include_match_details', '1');
  const url = API_BASE
    ? `${API_BASE}/related-background?${params.toString()}`
    : `/related-background?${params.toString()}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Related background failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetch best people to contact for a set of employee IDs.
 */
export async function getBestPeople(employeeIds = [], { destOrgId } = {}) {
  const ids = Array.isArray(employeeIds)
    ? employeeIds.map((v) => Number(v)).filter((v) => Number.isFinite(v))
    : [];
  if (ids.length === 0) return { work: [], college: [] };
  const url = API_BASE ? `${API_BASE}/best-people` : '/best-people';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      employee_ids: ids,
      dest_org_id: destOrgId != null ? Number(destOrgId) : undefined,
    }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load best people');
  }
  const data = await res.json();
  return (data && typeof data === 'object') ? data : { work: [], college: [], recruiters: [] };
}

export async function getTransitionPersonDetail(employeeId, { sourceOrgId, destOrgId, hop, startDate, endDate, connectionFilters } = {}) {
  const empId = employeeId != null ? Number(employeeId) : null;
  const source = sourceOrgId != null ? Number(sourceOrgId) : null;
  const dest = destOrgId != null ? Number(destOrgId) : null;
  const hopNum = hop != null ? Number(hop) : null;
  if (empId == null || Number.isNaN(empId)) throw new Error('employee_id is required');
  if (source == null || Number.isNaN(source)) throw new Error('source_org_id is required');
  if (dest == null || Number.isNaN(dest)) throw new Error('dest_org_id is required');
  if (hopNum == null || Number.isNaN(hopNum) || hopNum < 1) throw new Error('hop must be a positive integer');

  const params = new URLSearchParams({
    source_org_id: String(source),
    dest_org_id: String(dest),
    hop: String(hopNum),
  });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const cf = buildConnectionFiltersParam(connectionFilters);
  if (cf) params.set('connection_filters', cf);

  const url = API_BASE
    ? `${API_BASE}/employee-transitions/${empId}/detail?${params.toString()}`
    : `/employee-transitions/${empId}/detail?${params.toString()}`;

  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load transition person details');
  }
  return res.json();
}

export async function getRelatedPersonDetail(employeeId, { destOrgId, connectionFilters } = {}) {
  const empId = employeeId != null ? Number(employeeId) : null;
  const dest = destOrgId != null ? Number(destOrgId) : null;
  if (empId == null || Number.isNaN(empId)) throw new Error('employee_id is required');
  if (dest == null || Number.isNaN(dest)) throw new Error('dest_org_id is required');

  const params = new URLSearchParams({ dest_org_id: String(dest) });
  const cf = buildConnectionFiltersParam(connectionFilters);
  if (cf) params.set('connection_filters', cf);
  const url = API_BASE
    ? `${API_BASE}/related-background/${empId}/detail?${params.toString()}`
    : `/related-background/${empId}/detail?${params.toString()}`;

  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load related person details');
  }
  return res.json();
}

export async function getLinkedinOrgIdsByCompanyNames(companyNames = []) {
  const list = Array.isArray(companyNames)
    ? companyNames.map((n) => String(n || '').trim()).filter(Boolean)
    : [];
  if (list.length === 0) return { linkedin_org_ids: [], by_company: {} };

  const url = API_BASE ? `${API_BASE}/linkedin-org-ids` : '/linkedin-org-ids';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ company_names: list }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch linkedin org ids: ${res.status}`);
  }
  return res.json();
}

export async function getSimilarCompanies({ orgId, orgName, limit = 10 } = {}) {
  const params = new URLSearchParams();
  if (orgId != null && !Number.isNaN(Number(orgId))) params.set('org_id', String(Number(orgId)));
  if (orgName) params.set('org_name', String(orgName).trim());
  params.set('limit', String(Math.max(1, Math.min(10, Number(limit) || 10))));

  const url = API_BASE
    ? `${API_BASE}/similar-companies?${params.toString()}`
    : `/similar-companies?${params.toString()}`;

  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch similar companies: ${res.status}`);
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Auth Endpoints
 */
export async function requestOTP(email) {
  const url = API_BASE ? `${API_BASE}/request-otp` : '/request-otp';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to request OTP');
  }
  return res.json();
}

export async function verifyOTP(email, code, source = null) {
  const url = API_BASE ? `${API_BASE}/verify-otp` : '/verify-otp';
  const payload = { email, code };
  if (source) payload.source = source;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to verify OTP');
  }
  return res.json();
}

export async function logoutUser() {
  const url = API_BASE ? `${API_BASE}/logout` : '/logout';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to logout');
  }
  return res.json();
}

/**
 * Profile Endpoints
 */
const PROFILE_CACHE_KEY = 'insights_profile_cache_v1';
const PROFILE_CACHE_TTL_MS = 30 * 60 * 1000;

function normalizeProfilePayload(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.profile && typeof data.profile === 'object') return data.profile;
  return data;
}

export function getCachedProfile() {
  try {
    const raw = sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { timestamp, data } = parsed;
    if (!timestamp || Date.now() - Number(timestamp) > PROFILE_CACHE_TTL_MS) {
      sessionStorage.removeItem(PROFILE_CACHE_KEY);
      return null;
    }
    return data || null;
  } catch {
    return null;
  }
}

export function setCachedProfile(profileData) {
  const normalized = normalizeProfilePayload(profileData);
  if (!normalized) return null;
  try {
    sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
      timestamp: Date.now(),
      data: normalized,
    }));
  } catch {
    // ignore storage errors
  }
  return normalized;
}

export function clearCachedProfile() {
  try {
    sessionStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {
    // ignore storage errors
  }
}

export async function getProfile({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    const cached = getCachedProfile();
    if (cached) {
      return cached;
    }
  }
  const url = API_BASE ? `${API_BASE}/profile` : '/profile';
  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load profile');
  }
  const data = await res.json();
  setCachedProfile(data);
  return data;
}

export async function updateProfile(payload) {
  const url = API_BASE ? `${API_BASE}/profile` : '/profile';
  const res = await fetch(url, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save profile');
  }
  const data = await res.json();
  setCachedProfile(data);
  return data;
}

export async function uploadResume(file) {
  const url = API_BASE ? `${API_BASE}/profile/resume` : '/profile/resume';
  const formData = new FormData();
  formData.append('resume', file);

  const headers = getAuthHeaders();
  // Let the browser set proper multipart boundary
  if (headers['Content-Type']) {
    delete headers['Content-Type'];
  }

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: formData
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to upload resume');
  }
  const data = await res.json();
  setCachedProfile(data);
  return data;
}

export async function deleteResume() {
  const url = API_BASE ? `${API_BASE}/profile/resume` : '/profile/resume';
  const res = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove resume');
  }
  const data = await res.json();
  setCachedProfile(data);
  return data;
}

export async function addNotifyRequest(companyName) {
  const url = API_BASE ? `${API_BASE}/notify-request` : '/notify-request';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ company_name: companyName }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save notification request');
  }
  return res.json();
}

export async function addReferral(referralLink) {
  const url = API_BASE ? `${API_BASE}/referral` : '/referral';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ referral_link: referralLink }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save referral');
  }
  return res.json();
}

export async function downloadResume(filename) {
  const url = API_BASE ? `${API_BASE}/profile/resume` : '/profile/resume';
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('insights_token')}`
    }
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to download resume');
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'resume.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export async function getAlumniDetail(employeeId, { orgId, startDate, endDate } = {}) {
  const id = employeeId != null ? Number(employeeId) : null;
  const org = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) throw new Error('employee_id is required');
  if (org == null || Number.isNaN(org)) throw new Error('org_id is required');
  const params = new URLSearchParams({ org_id: String(org) });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const url = API_BASE
    ? `${API_BASE}/alumni/${id}/detail?${params.toString()}`
    : `/alumni/${id}/detail?${params.toString()}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load alumni details');
  }
  return res.json();
}

export async function getEmployeeDetail(employeeId, { orgId } = {}) {
  const id = employeeId != null ? Number(employeeId) : null;
  const org = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) throw new Error('employee_id is required');
  if (org == null || Number.isNaN(org)) throw new Error('org_id is required');
  const url = API_BASE
    ? `${API_BASE}/employees/${id}/detail?org_id=${encodeURIComponent(String(org))}`
    : `/employees/${id}/detail?org_id=${encodeURIComponent(String(org))}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load employee details');
  }
  return res.json();
}
/**
 * Fetch the 5 focused insight panels for the intermediate insight screen.
 * Returns: active_hirers, next_destinations, career_paths, best_tenure, role_hiring.
 */
const _insightsInflight = new Map();
export async function getCompanyInsights(
  orgId,
  { startDate, endDate, role, location, includeCore = true, includeNetwork = true, includeRole = true, includeLocation = true, networkMode, networkTab, networkTabId, timeoutMs } = {}
) {
  const id = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) {
    throw new Error('org_id is required');
  }
  const params = new URLSearchParams({ org_id: String(id) });
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (role) params.set('role', role);
  if (location) params.set('location', location);
  params.set('include_core', includeCore ? '1' : '0');
  params.set('include_network', includeNetwork ? '1' : '0');
  params.set('include_role', includeRole ? '1' : '0');
  params.set('include_location', includeLocation ? '1' : '0');
  if (networkMode) params.set('network_mode', String(networkMode));
  if (networkTab) params.set('network_tab', String(networkTab));
  if (networkTabId != null && Number.isFinite(Number(networkTabId))) {
    params.set('network_tab_id', String(Number(networkTabId)));
  }
  const url = API_BASE
    ? `${API_BASE}/company-insights?${params.toString()}`
    : `/company-insights?${params.toString()}`;

  // Dedup: if an identical request is already in-flight, return the same promise
  if (_insightsInflight.has(url)) {
    return _insightsInflight.get(url);
  }

  const doFetch = async () => {
    const controller = new AbortController();
    const effectiveTimeout = Number(timeoutMs || 0);
    let timeoutId = null;
    if (effectiveTimeout > 0) {
      timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);
    }
    let res;
    try {
      res = await fetch(url, { headers: getAuthHeaders(), signal: controller.signal });
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw new Error('Company insights request timed out. Please try again.');
      }
      throw err;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
    if (!res.ok) {
      if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Company insights failed: ${res.status}`);
    }
    return res.json();
  };

  const promise = doFetch().finally(() => {
    _insightsInflight.delete(url);
  });
  _insightsInflight.set(url, promise);
  return promise;
}

/**
 * Fetch lean job listings for a company.
 * Returns: { jobs: [...], total: N }
 * Each job: { id, role, location, employment_type, seniority, job_function, posted_at, job_url, skills }
 */
export async function getCompanyJobs(orgId, { limit = 10, offset = 0 } = {}) {
  const id = orgId != null ? Number(orgId) : null;
  if (id == null || Number.isNaN(id)) throw new Error('org_id is required');
  const params = new URLSearchParams({
    org_id: String(id),
    limit: String(limit),
    offset: String(offset),
  });
  const url = API_BASE
    ? `${API_BASE}/company-jobs?${params.toString()}`
    : `/company-jobs?${params.toString()}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Company jobs failed: ${res.status}`);
  }
  return res.json();
}

export async function getCompanyJobDetail(jobId) {
  const id = jobId != null ? Number(jobId) : null;
  if (id == null || Number.isNaN(id)) throw new Error('job_id is required');
  const params = new URLSearchParams({ job_id: String(id) });
  const url = API_BASE
    ? `${API_BASE}/company-job-detail?${params.toString()}`
    : `/company-job-detail?${params.toString()}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Company job detail failed: ${res.status}`);
  }
  return res.json();
}

export async function getCompanyJobsSummary(orgIds = [], { includeMatch = true } = {}) {
  const ids = Array.isArray(orgIds) ? orgIds.filter((v) => v != null) : [];
  const url = API_BASE ? `${API_BASE}/company-jobs-summary` : '/company-jobs-summary';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ org_ids: ids, include_match: !!includeMatch }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Company jobs summary failed: ${res.status}`);
  }
  return res.json();
}

export async function getMailerDigestJobs({
  digestToken,
  offset = 0,
  limit = 10,
  search = '',
  locations = [],
  filters = [],
  profileMatchOnly = false,
  sortBy = 'best_match',
}) {
  const token = String(digestToken || '').trim();
  if (!token) throw new Error('Digest token is required');
  const url = API_BASE ? `${API_BASE}/mailer/digest-jobs` : '/mailer/digest-jobs';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      digest: token,
      offset,
      limit,
      search: String(search || '').trim(),
      locations: Array.isArray(locations) ? locations : [],
      filters: Array.isArray(filters) ? filters : [],
      profile_match_only: !!profileMatchOnly,
      sort_by: String(sortBy || 'best_match'),
    }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Mailer digest failed: ${res.status}`);
  }
  return res.json();
}

export async function getMailerMyJobs() {
  const url = API_BASE ? `${API_BASE}/mailer/my-jobs` : '/mailer/my-jobs';
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Jobs feed failed: ${res.status}`);
  }
  return res.json();
}

export async function getJobsFeedPage({
  offset = 0,
  limit = 10,
  filters = [],
  destinationOrgIds = [],
  search = '',
  locations = [],
  profileMatchOnly = false,
  sortBy = 'best_match',
  timeoutMs = 150000,
} = {}) {
  const url = API_BASE ? `${API_BASE}/jobs/feed-page` : '/jobs/feed-page';
  const controller = new AbortController();
  const effectiveTimeout = Number(timeoutMs || 0);
  let timeoutId = null;
  if (effectiveTimeout > 0) {
    timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);
  }
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      signal: controller.signal,
      body: JSON.stringify({
        offset,
        limit,
        filters: Array.isArray(filters) ? filters : [],
        destination_org_ids: Array.isArray(destinationOrgIds) ? destinationOrgIds : [],
        search: String(search || '').trim(),
        locations: Array.isArray(locations) ? locations : [],
        profile_match_only: !!profileMatchOnly,
        sort_by: String(sortBy || 'best_match'),
      }),
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('Jobs feed request timed out. Please try again.');
    }
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Jobs feed page failed: ${res.status}`);
  }
  return res.json();
}

export async function setJobStatus(jobId, status) {
  const url = API_BASE ? `${API_BASE}/jobs/status` : '/jobs/status';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ job_id: jobId, status }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Job status update failed: ${res.status}`);
  }
  return res.json();
}

export async function getJobsTracker({ status = '', offset = 0, limit = 10 } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', String(status));
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  const qs = params.toString();
  const url = API_BASE ? `${API_BASE}/jobs/tracker?${qs}` : `/jobs/tracker?${qs}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Job tracker failed: ${res.status}`);
  }
  return res.json();
}

export async function listJobStatuses(statusFilter) {
  const params = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
  const url = API_BASE ? `${API_BASE}/jobs/status${params}` : `/jobs/status${params}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Job statuses failed: ${res.status}`);
  }
  return res.json();
}

export async function captureOrgDestinationMap({
  sourceOrgId,
  destinationOrgIds = [],
  reasonType,
  mapType = 'role',
  referenceName = '',
  referenceId = undefined,
} = {}) {
  const src = Number(sourceOrgId);
  if (!Number.isFinite(src)) throw new Error('source_org_id is required');
  const dest = Array.isArray(destinationOrgIds)
    ? destinationOrgIds.map((x) => Number(x)).filter((x) => Number.isFinite(x))
    : [];
  if (!reasonType) throw new Error('reason_type is required');
  const url = API_BASE ? `${API_BASE}/org-destination-map/capture` : '/org-destination-map/capture';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      source_org_id: src,
      destination_org_ids: dest,
      reason_type: String(reasonType),
      map_type: String(mapType || 'role'),
      reference_name: String(referenceName || ''),
      reference_id: referenceId != null ? Number(referenceId) : undefined,
    }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Capture failed: ${res.status}`);
  }
  return res.json();
}

export async function precheckOrgDestinationMapCapture({
  sourceOrgId,
  blocks = [],
} = {}) {
  const src = Number(sourceOrgId);
  if (!Number.isFinite(src)) throw new Error('source_org_id is required');
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  const url = API_BASE ? `${API_BASE}/org-destination-map/capture-precheck` : '/org-destination-map/capture-precheck';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      source_org_id: src,
      blocks: safeBlocks,
    }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Capture precheck failed: ${res.status}`);
  }
  return res.json();
}

export async function exportAggregatedJobMatches({
  startDate,
  endDate,
  role,
} = {}) {
  const createUrl = API_BASE ? `${API_BASE}/aggregated-job-matches/export` : '/aggregated-job-matches/export';
  const payload = {};
  if (startDate) payload.start_date = startDate;
  if (endDate) payload.end_date = endDate;
  if (role) payload.role = role;

  const createRes = await fetch(createUrl, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!createRes.ok) {
    if (createRes.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await createRes.json().catch(() => ({}));
    throw new Error(err.error || `Export creation failed: ${createRes.status}`);
  }

  const createData = await createRes.json();
  const downloadPath = createData?.download_url;
  if (!downloadPath) {
    throw new Error('Export created but download URL missing');
  }

  const downloadUrl = API_BASE ? `${API_BASE}${downloadPath}` : downloadPath;
  const downloadRes = await fetch(downloadUrl, { headers: getAuthHeaders() });
  if (!downloadRes.ok) {
    if (downloadRes.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await downloadRes.json().catch(() => ({}));
    throw new Error(err.error || `Export download failed: ${downloadRes.status}`);
  }

  const blob = await downloadRes.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = createData?.filename || 'aggregated_job_matches.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);

  return createData;
}

export async function submitFeedback(rating, comment = '') {
  const url = API_BASE ? `${API_BASE}/feedback` : '/feedback';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ rating, comment }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to submit feedback');
  }
  return res.json();
}

export async function getFeedbackStatus() {
  const url = API_BASE ? `${API_BASE}/feedback/status` : '/feedback/status';
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load feedback status');
  }
  const data = await res.json();
  return Boolean(data?.has_feedback);
}

export async function createCashfreeOrder({ amount, currency = 'INR', customerPhone } = {}) {
  const amountRupees = Number(amount);
  if (!Number.isFinite(amountRupees) || amountRupees < 1) {
    throw new Error('Amount must be at least ₹1');
  }
  const url = API_BASE ? `${API_BASE}/api/cashfree/create-order` : '/api/cashfree/create-order';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      amount: Number(amountRupees.toFixed(2)),
      currency,
      customer_phone: customerPhone,
    }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Create Cashfree order failed: ${res.status}`);
  }
  return res.json();
}

export async function verifyCashfreeOrder(orderId) {
  if (!orderId) {
    throw new Error('orderId is required');
  }
  const url = API_BASE ? `${API_BASE}/api/cashfree/verify-order` : '/api/cashfree/verify-order';
  const res = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      order_id: orderId,
    }),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Verify Cashfree order failed: ${res.status}`);
  }
  return res.json();
}

export async function getOrgMatchReasons(orgId) {
  const url = API_BASE ? `${API_BASE}/orgs/${orgId}/reasons` : `/orgs/${orgId}/reasons`;
  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    if (res.status === 401) { window.dispatchEvent(new CustomEvent('auth-error')); }
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Fetch reasons failed: ${res.status}`);
  }
  return res.json();
}
